import { ChangeDetectionStrategy, Component, Inject, OnInit } from "@angular/core";
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from "@angular/material";
import { ImportLicense, LicenseDialogService } from "./licenseDialog.service";
import { environment } from "../../../../environments/environment";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ExternalLinksSettingsService } from "../../services/ExternalLinksSettings.service";

const licenseMap: { [key in ImportLicense["license"]]: string } = {
  "MIT": "mit",
  "Apache-2.0": "apache",
  "0BSD": "0bsd",
  "(OFL-1.1 AND MIT)": "silplusmit",
  "ISC": "mit",
}

@Component({
  selector: "licenseDialog",
  templateUrl: "./licenseDialog.component.html",
  styleUrls: ["./licenseDialog.component.scss"],
  providers: [LicenseDialogService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * LicenseDialogComponent displays a list of third-party licenses used in the app.
 * Users can expand individual licenses to view the full license text.
 */
export class LicenseDialogComponent implements OnInit {
  public licenses: ImportLicense[] = [];
  public expandedLicenses = new Set<ImportLicense>();
  private licenseURL: string;

  constructor(
    public bottomSheetRef: MatBottomSheetRef<LicenseDialogComponent>,
    private licenseService: LicenseDialogService,
    private sanitizer: DomSanitizer,
    private externalLinksSettingsService: ExternalLinksSettingsService,
    @Inject(MAT_BOTTOM_SHEET_DATA) public isLicenseDialog: boolean) {
  }

  ngOnInit() {
    const me = this;

    this.licenses = me.licenseService.getLicenses();
    this.licenseURL = environment.YourAppServer.BaseURI + "licenses";
  }

  /**
   * Toggles the expanded state of a given license entry.
   * @param license - The license to expand or collapse
   */
  public toggleLicense(license: ImportLicense): void {
    if (this.expandedLicenses.has(license)) {
      this.expandedLicenses.delete(license);
    } else {
      this.expandedLicenses.add(license);
    }
  }

  /**
  * Converts a git-based repository URL into a usable https URL.
  * @param url - The raw repository URL (e.g., "git+https://...")
  * @returns Sanitized https URL string
  */
  public getSafeRepositoryUrl(url: string): string {
    let safeUrl = url.replace(/^git\+/, "");
    safeUrl = safeUrl.replace(/\.git$/, "");
    safeUrl = safeUrl.replace(/^git:\/\//, "https://");
    safeUrl = safeUrl.replace(/^ssh:\/\/git@github.com\//, "https://github.com/");

    return safeUrl;
  }

  /**
   * Generates a URL for the license file based on the license type.
   * @param license - The license object containing the license type
   * @returns URL string for the license file
   */
  public getLicenseFileURL(license: ImportLicense): string {
    let licenseFile = "";

    licenseFile = licenseMap[license.license] || "";
    return `${this.licenseURL}/${licenseFile}.txt`;
  }

  /**
  * Opens an external link with confirmation prompt.
  * @param targetUrl The full external git URL to navigate to.
  */
  public setTargetNavigation(targetUrl: string): void {
    this.externalLinksSettingsService.RouteToExternalLinks(this.getSafeRepositoryUrl(targetUrl), true);
  }

  public getSanitizedLicenseUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  public closeBottomSheet(): void {
    this.bottomSheetRef.dismiss(true);
  }
}